import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import type { NorthstarBlockedMovement, NorthstarKpi } from '../../types';
import ProvenanceBadge from './ProvenanceBadge';
import { formatEvoValue } from './format';
import CascadeTrace from './CascadeTrace';
import { C2_EVOLUTION_ENABLED } from '../../evolutionFlag';

export default function NorthstarApexCard() {
  const q = useQuery({ queryKey: ['evolution', 'northstar', 'live'], queryFn: () => api.evolutionNorthstarLive() });

  if (q.isLoading) {
    return (
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="small faint">Loading Northstar...</div>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="small" style={{ color: 'var(--red)' }}>Apex unavailable.</div>
      </div>
    );
  }

  const d = q.data;
  const moverHref = driverHref(d.largest_mover.driver_node_id);
  return (
    <>
    <div className="panel" style={{ marginBottom: 16, borderLeft: '3px solid var(--azura)' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Northstar
          </div>
          <div style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 700 }}>Top value drivers</div>
        </div>
        <span className={`status-pill ${d.binding_constraints.length > 0 ? 'red' : 'green'}`}>
          {d.binding_constraints.length > 0 ? 'binding constraints' : 'constraints clear'}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          marginTop: 12,
        }}
      >
        {d.kpis.map((kpi) => (
          <NorthstarKpiTile key={kpi.key} kpi={kpi} />
        ))}
      </div>

      <div className="row" style={{ justifyContent: 'space-between', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        <div className="small" style={{ color: 'var(--muted)', maxWidth: 760, lineHeight: 1.45 }}>
          Largest mover: <b style={{ color: 'var(--ink)' }}>{d.largest_mover.label}</b>{' '}
          <span className="mono">{formatEvoValue(d.largest_mover.delta, { signed: true, compact: true })}</span>
          {d.largest_mover.attribution && (
            <span> · {d.largest_mover.attribution}</span>
          )}
          {d.missing_drivers.length > 0 && (
            <span className="tag amber" style={{ marginLeft: 8 }}>driver unavailable</span>
          )}
        </div>
        <Link to={moverHref} className="btn ghost" style={{ textDecoration: 'none', fontSize: 12 }}>
          Drill into drivers -&gt;
        </Link>
      </div>

      {d.blocked_movements.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          {d.blocked_movements.map((movement) => (
            <BlockedMovementRow key={`${movement.move_id}-${movement.constraint.constraint_id}`} movement={movement} />
          ))}
        </div>
      )}

      <div className="panel" style={{ marginTop: 12, padding: '10px 12px', background: 'var(--panel2)' }}>
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`tag ${d.blocked_movements.length > 0 ? 'red' : 'olive'}`}>
            {d.blocked_movements.length > 0 ? 'BLOCKED' : 'CLEAR'}
          </span>
          <span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{d.headline}</span>
          <span className="small faint">hard gates watched</span>
        </div>
      </div>
    </div>
      {/* Cascade trace embedded alongside the apex (evolution suite; flag-gated). */}
      {C2_EVOLUTION_ENABLED && (
        <section style={{ marginBottom: 16 }}>
          <CascadeTrace />
        </section>
      )}
    </>
  );
}

function BlockedMovementRow({ movement }: { movement: NorthstarBlockedMovement }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        border: '1px solid color-mix(in srgb, var(--red) 42%, var(--line))',
        borderRadius: 8,
        background: 'rgba(207, 84, 64, 0.1)',
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="tag red">BLOCKED · {movement.constraint.constraint_id}</span>
        <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{movement.move_id}</span>
        <span className="small faint">{movement.constraint.label}</span>
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 6 }}>
        <span className="small faint">attempted</span>
        <span className="mono" style={{ color: 'var(--red)' }}>{formatEvoValue(movement.attempted_delta, { signed: true, compact: true })}</span>
        <span className="small faint">counted</span>
        <span className="mono" style={{ color: 'var(--muted)' }}>{formatEvoValue(movement.applied_delta, { signed: true, compact: true })}</span>
        <Link to={driverHref(movement.driver_node_id)} className="btn ghost" style={{ textDecoration: 'none', fontSize: 12 }}>
          driver -&gt;
        </Link>
      </div>
      <div className="small" style={{ color: 'var(--muted)', marginTop: 6, lineHeight: 1.45 }}>{movement.reason}</div>
    </div>
  );
}

function NorthstarKpiTile({ kpi }: { kpi: NorthstarKpi }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 7,
        minWidth: 0,
        minHeight: 112,
        padding: '10px 12px',
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: 'color-mix(in srgb, var(--panel2) 76%, transparent)',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <Link
        to={driverHref(kpi.driver_node_id)}
        aria-label={`${kpi.label} driver tree`}
        style={{ display: 'grid', gap: 7, minWidth: 0, color: 'inherit', textDecoration: 'none' }}
      >
        <div style={{ color: 'var(--ink)', fontWeight: 700, lineHeight: 1.25 }}>{kpi.label}</div>
        <span className="mono" style={{ fontSize: 19, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.1 }}>
          {formatEvoValue(kpi.value, { compact: true })}
        </span>
        <div className="row" style={{ gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span className="small faint">vs plan</span>
          <span
            className="mono"
            style={{
              color: kpi.delta_vs_plan.value >= 0 ? 'var(--olive)' : 'var(--red)',
              fontSize: 13,
            }}
          >
            {formatEvoValue(kpi.delta_vs_plan, { signed: true, compact: true })}
          </span>
        </div>
      </Link>
      <div className="row" style={{ gap: 4, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="small faint">value</span>
        <ProvenanceBadge value={kpi.value} labelMode="calibration" revealOnHover />
      </div>
      <div className="row" style={{ gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="small faint">delta</span>
        <ProvenanceBadge value={kpi.delta_vs_plan} labelMode="calibration" revealOnHover />
      </div>
    </div>
  );
}

function driverHref(driverNodeId: string) {
  return `/spine?driver=${encodeURIComponent(driverNodeId)}`;
}
